import { Check } from "lucide-react";
import { compareWorkspaces } from "@tc-superpods/app-contract";
import { useState, type JSX } from "react";
import { BrandMark, GridDotsIcon } from "./BrandMark";
import { cn } from "./lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from "./ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import type { Workspace } from "../types";

export interface WorkspaceSwitcherProps {
  workspaces: Workspace[];
  activeWorkspaceId: string | null;
  hovered: boolean;
  collapsed: boolean;
  onSelectWorkspace: (workspaceId: string) => void;
}

export function WorkspaceSwitcher({
  workspaces,
  activeWorkspaceId,
  hovered,
  collapsed,
  onSelectWorkspace
}: WorkspaceSwitcherProps): JSX.Element {
  const [open, setOpen] = useState(false);
  const showGrid = hovered || open;
  const sortedWorkspaces = [...workspaces].sort(compareWorkspaces);
  const activeWorkspace = workspaces.find((workspace) => workspace.id === activeWorkspaceId);

  const markClasses = cn(
    "flex size-8 shrink-0 items-center justify-center rounded-sm text-sidebar-text transition-colors",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
    "focus-visible:ring-offset-2 focus-visible:ring-offset-shell-canvas",
    showGrid && "bg-nav-active"
  );

  const trigger = (
    <DropdownMenuTrigger asChild>
      <button
        type="button"
        aria-label="Change workspace"
        data-platform-workspace-switcher=""
        className={cn(
          "flex items-center gap-3 rounded-md font-body text-left transition-colors",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          collapsed ? "justify-center" : "w-full"
        )}
      >
        <span className={markClasses}>{showGrid ? <GridDotsIcon /> : <BrandMark />}</span>
        {!collapsed && (
          <span className="flex min-w-0 flex-col leading-tight">
            <span className="truncate text-sm font-semibold text-sidebar-text">
              {activeWorkspace?.label ?? "Workspace"}
            </span>
            <span className="text-section-label font-medium uppercase tracking-wide text-sidebar-text-muted">
              Workspace
            </span>
          </span>
        )}
      </button>
    </DropdownMenuTrigger>
  );

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      {collapsed ? (
        <Tooltip>
          <TooltipTrigger asChild>{trigger}</TooltipTrigger>
          <TooltipContent side="right">Change workspace</TooltipContent>
        </Tooltip>
      ) : (
        trigger
      )}

      <DropdownMenuContent align="start" className="min-w-56">
        <DropdownMenuLabel>Workspace</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {sortedWorkspaces.map((workspace) => {
          const isActive = workspace.id === activeWorkspaceId;

          return (
            <DropdownMenuItem
              key={workspace.id}
              data-platform-workspace-choice={workspace.id}
              onSelect={() => onSelectWorkspace(workspace.id)}
              className={cn(isActive && "font-medium")}
            >
              <Check className={cn(isActive ? "opacity-100" : "opacity-0")} aria-hidden="true" />
              {workspace.icon ? (
                <img
                  src={workspace.icon}
                  alt=""
                  aria-hidden="true"
                  width={16}
                  height={16}
                  className="size-4 shrink-0 object-contain"
                />
              ) : null}
              <span>{workspace.label}</span>
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
